import { useMemo } from 'react';

// Map IANA timezones to ISO country codes (EU member states)
// Includes all timezones for overseas territories subject to GDPR
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  // Austria
  'Europe/Vienna': 'AT',
  // Belgium
  'Europe/Brussels': 'BE',
  // Bulgaria
  'Europe/Sofia': 'BG',
  // Croatia
  'Europe/Zagreb': 'HR',
  // Cyprus
  'Asia/Nicosia': 'CY',
  'Asia/Famagusta': 'CY',
  // Czechia
  'Europe/Prague': 'CZ',
  // Denmark (Greenland and Faroe Islands are not EU but Denmark proper is)
  'Europe/Copenhagen': 'DK',
  // Estonia
  'Europe/Tallinn': 'EE',
  // Finland
  'Europe/Helsinki': 'FI',
  'Europe/Mariehamn': 'FI', // Åland Islands
  // France (including overseas departments subject to GDPR)
  'Europe/Paris': 'FR',
  'America/Guadeloupe': 'FR',
  'America/Martinique': 'FR',
  'America/Cayenne': 'FR', // French Guiana
  'Indian/Reunion': 'FR',
  'Indian/Mayotte': 'FR',
  'America/Miquelon': 'FR', // Saint Pierre and Miquelon
  // Germany
  'Europe/Berlin': 'DE',
  'Europe/Busingen': 'DE',
  // Greece
  'Europe/Athens': 'GR',
  // Hungary
  'Europe/Budapest': 'HU',
  // Ireland
  'Europe/Dublin': 'IE',
  // Italy
  'Europe/Rome': 'IT',
  // Latvia
  'Europe/Riga': 'LV',
  // Lithuania
  'Europe/Vilnius': 'LT',
  // Luxembourg
  'Europe/Luxembourg': 'LU',
  // Malta
  'Europe/Malta': 'MT',
  // Netherlands
  'Europe/Amsterdam': 'NL',
  // Poland
  'Europe/Warsaw': 'PL',
  // Portugal
  'Europe/Lisbon': 'PT',
  'Atlantic/Azores': 'PT',
  'Atlantic/Madeira': 'PT',
  // Romania
  'Europe/Bucharest': 'RO',
  // Slovakia
  'Europe/Bratislava': 'SK',
  // Slovenia
  'Europe/Ljubljana': 'SI',
  // Spain
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',
  'Africa/Ceuta': 'ES',
  // Sweden
  'Europe/Stockholm': 'SE',

  // === EEA Countries (non-EU, subject to GDPR) ===
  // Iceland
  'Atlantic/Reykjavik': 'IS',
  // Liechtenstein
  'Europe/Vaduz': 'LI',
  // Norway
  'Europe/Oslo': 'NO',
  'Arctic/Longyearbyen': 'NO', // Svalbard

  // === UK (UK GDPR post-Brexit) ===
  'Europe/London': 'GB',
  'Europe/Jersey': 'GB',
  'Europe/Guernsey': 'GB',
  'Europe/Isle_of_Man': 'GB',
  'Atlantic/St_Helena': 'GB',
  'Atlantic/Falkland': 'GB',
  'Atlantic/South_Georgia': 'GB',
  'Indian/Chagos': 'GB',
  'Pacific/Pitcairn': 'GB',

  // === Other GDPR-adequate countries (apply equivalent protections) ===
  // Switzerland (Federal Act on Data Protection)
  'Europe/Zurich': 'CH',
};

const getCountryFromTimezone = (): string | null => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_TO_COUNTRY[timezone] ?? null;
  } catch {
    return null;
  }
};

export function useUserCountry() {
  const countryCode = useMemo(() => getCountryFromTimezone(), []);
  return { countryCode, isLoading: false };
}
